import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview Weekly Schedule Matrix Component
 * @module @boosting/ui-kit/components/WeeklyScheduleMatrix
 *
 * Interactive matrix for selecting working hours per day of week.
 *
 * THEMING: Uses semantic color classes.
 * Required Tailwind colors: primary, success, warning, slate
 */
import { useState, useCallback } from 'react';
const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const DAYS_FULL = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
export function WeeklyScheduleMatrix({ schedule, onChange, disabled }) {
    const [isDragging, setIsDragging] = useState(false);
    const [dragMode, setDragMode] = useState('enable');
    const isHourEnabled = (day, hour) => {
        return schedule[day]?.includes(hour) ?? false;
    };
    const toggleHour = useCallback((day, hour, forceMode) => {
        if (disabled)
            return;
        const currentHours = schedule[day] || [];
        const isEnabled = currentHours.includes(hour);
        const mode = forceMode ?? (isEnabled ? 'disable' : 'enable');
        let newHours;
        if (mode === 'enable' && !isEnabled) {
            newHours = [...currentHours, hour].sort((a, b) => a - b);
        }
        else if (mode === 'disable' && isEnabled) {
            newHours = currentHours.filter(h => h !== hour);
        }
        else {
            return;
        }
        onChange({ ...schedule, [day]: newHours });
    }, [schedule, onChange, disabled]);
    const handleMouseDown = (day, hour) => {
        if (disabled)
            return;
        const isEnabled = isHourEnabled(day, hour);
        setDragMode(isEnabled ? 'disable' : 'enable');
        setIsDragging(true);
        toggleHour(day, hour);
    };
    const handleMouseEnter = (day, hour) => {
        if (isDragging && !disabled) {
            toggleHour(day, hour, dragMode);
        }
    };
    const handleMouseUp = () => {
        setIsDragging(false);
    };
    // Quick actions
    const selectWorkingHours = () => {
        const newSchedule = {};
        for (let day = 0; day < 5; day++) { // Lun-Vie
            newSchedule[day] = Array.from({ length: 9 }, (_, i) => i + 9); // 9-17
        }
        newSchedule[5] = [];
        newSchedule[6] = [];
        onChange(newSchedule);
    };
    const selectAllHours = () => {
        const newSchedule = {};
        for (let day = 0; day < 7; day++) {
            newSchedule[day] = [...HOURS];
        }
        onChange(newSchedule);
    };
    const clearAll = () => {
        onChange({ 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] });
    };
    const selectDay = (day) => {
        const currentHours = schedule[day] || [];
        const isFullDay = currentHours.length === 24;
        onChange({
            ...schedule,
            [day]: isFullDay ? [] : [...HOURS]
        });
    };
    const selectHourAllDays = (hour) => {
        const newSchedule = { ...schedule };
        const allEnabled = DAYS.every((_, i) => schedule[i]?.includes(hour));
        for (let day = 0; day < 7; day++) {
            const currentHours = schedule[day] || [];
            if (allEnabled) {
                newSchedule[day] = currentHours.filter(h => h !== hour);
            }
            else if (!currentHours.includes(hour)) {
                newSchedule[day] = [...currentHours, hour].sort((a, b) => a - b);
            }
        }
        onChange(newSchedule);
    };
    // Count enabled hours
    const totalEnabled = Object.values(schedule).reduce((sum, hours) => sum + (hours?.length || 0), 0);
    return (_jsxs("div", { className: "space-y-3", onMouseUp: handleMouseUp, onMouseLeave: handleMouseUp, children: [_jsxs("div", { className: "flex items-center gap-2 text-sm", children: [_jsx("span", { className: "text-slate-500", children: "Acciones r\u00E1pidas:" }), _jsx("button", { onClick: selectWorkingHours, className: "px-2 py-1 bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 rounded hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors", disabled: disabled, children: "Horario laboral (L-V 9-18)" }), _jsx("button", { onClick: selectAllHours, className: "px-2 py-1 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors", disabled: disabled, children: "Todo" }), _jsx("button", { onClick: clearAll, className: "px-2 py-1 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors", disabled: disabled, children: "Limpiar" }), _jsxs("span", { className: "ml-auto text-slate-400", children: [totalEnabled, " horas/semana activas"] })] }), _jsx("div", { className: "overflow-x-auto", children: _jsx("div", { className: "inline-block min-w-full", children: _jsxs("div", { className: "flex", children: [_jsxs("div", { className: "w-10 flex-shrink-0", children: [_jsx("div", { className: "h-8" }), " ", HOURS.map(hour => (_jsx("button", { onClick: () => selectHourAllDays(hour), className: "h-5 w-full text-xs text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors text-right pr-1", disabled: disabled, title: `Toggle ${hour.toString().padStart(2, '0')}:00 todos los días`, children: hour.toString().padStart(2, '0') }, hour)))] }), DAYS.map((day, dayIndex) => (_jsxs("div", { className: "flex-1 min-w-[40px]", children: [_jsx("button", { onClick: () => selectDay(dayIndex), className: `h-8 w-full text-xs font-medium rounded-t transition-colors ${dayIndex >= 5
                                            ? 'text-warning-600 dark:text-warning-400 hover:bg-warning-100 dark:hover:bg-warning-900/30'
                                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`, disabled: disabled, title: `Toggle ${DAYS_FULL[dayIndex]} completo`, children: day }), HOURS.map(hour => {
                                        const enabled = isHourEnabled(dayIndex, hour);
                                        return (_jsx("div", { onMouseDown: () => handleMouseDown(dayIndex, hour), onMouseEnter: () => handleMouseEnter(dayIndex, hour), className: `h-5 border border-slate-100 dark:border-slate-700 cursor-pointer transition-colors ${enabled
                                                ? dayIndex >= 5
                                                    ? 'bg-warning-400 hover:bg-warning-500 dark:bg-warning-600 dark:hover:bg-warning-500'
                                                    : 'bg-success-400 hover:bg-success-500 dark:bg-success-600 dark:hover:bg-success-500'
                                                : 'bg-slate-50 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700'} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`, title: `${DAYS_FULL[dayIndex]} ${hour.toString().padStart(2, '0')}:00` }, hour));
                                    })] }, day)))] }) }) }), _jsxs("div", { className: "flex items-center gap-4 text-xs text-slate-500", children: [_jsxs("div", { className: "flex items-center gap-1", children: [_jsx("div", { className: "w-3 h-3 bg-success-400 dark:bg-success-600 rounded" }), _jsx("span", { children: "Activo (L-V)" })] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("div", { className: "w-3 h-3 bg-warning-400 dark:bg-warning-600 rounded" }), _jsx("span", { children: "Activo (Fin de semana)" })] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("div", { className: "w-3 h-3 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded" }), _jsx("span", { children: "Inactivo" })] })] })] }));
}
/**
 * Default schedule: Monday-Friday 9:00-18:00
 */
export function getDefaultSchedule() {
    const schedule = {};
    for (let day = 0; day < 5; day++) {
        schedule[day] = Array.from({ length: 9 }, (_, i) => i + 9); // 9-17
    }
    schedule[5] = [];
    schedule[6] = [];
    return schedule;
}
/**
 * Check if a given datetime is within the schedule
 */
export function isWithinSchedule(schedule, date = new Date()) {
    // JavaScript: 0=Sunday, 1=Monday... we need to convert
    const jsDay = date.getDay();
    const ourDay = jsDay === 0 ? 6 : jsDay - 1; // Convert to 0=Monday
    const hour = date.getHours();
    return schedule[ourDay]?.includes(hour) ?? false;
}
export default WeeklyScheduleMatrix;
